import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { createStore } from '@xstate/store'
import { useSelector } from '@xstate/store/react'
import { BodyText } from 'design-configuration/components'
import { AnimatePresence, motion, Variants } from 'motion/react'

type TransitionAnimation = 'to-start' | 'to-end' | 'from-start' | 'from-end'

const navigationMenuStore = createStore({
  context: {
    activeItem: undefined as string | undefined,
    prevActiveItem: undefined as string | undefined,
    items: [] as string[]
  },
  on: {
    setActiveItem: (context, event: { activeItem: string }) => {
      return {
        ...context,
        activeItem: event.activeItem,
        prevActiveItem: context.activeItem
      }
    },
    registerItem: (context, event: { item: string }) => {
      return {
        ...context,
        items: [...context.items, event.item]
      }
    },
    unregisterItem: (context, event: { item: string }) => {
      return {
        ...context,
        items: context.items.filter((item) => item !== event.item)
      }
    }
  }
})

const NavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, children, ...props }, ref) => {
  const activeItem = useSelector(navigationMenuStore, (state) => state.context.activeItem)

  return (
    <NavigationMenuPrimitive.Root
      ref={ref}
      className={cn('relative z-10 flex max-w-max flex-1 items-center justify-center', className)}
      onValueChange={(value) => {
        navigationMenuStore.send({ type: 'setActiveItem', activeItem: value })
      }}
      value={activeItem}
      {...props}
    >
      {children}
      {/* <NavigationMenuViewport /> */}
    </NavigationMenuPrimitive.Root>
  )
})
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, children, ...props }, ref) => {
  return (
    <NavigationMenuPrimitive.List
      ref={ref}
      className={cn('gap-xs group flex flex-1 list-none items-center justify-center', className)}
      {...props}
    >
      {children}
    </NavigationMenuPrimitive.List>
  )
})
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName

const NavigationMenuItem = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, value, ...props }, ref) => {
  React.useEffect(() => {
    const itemValue = value ?? Math.random().toString()
    navigationMenuStore.send({ type: 'registerItem', item: itemValue })

    return () => {
      navigationMenuStore.send({ type: 'unregisterItem', item: itemValue })
    }
  }, [value])

  return <NavigationMenuPrimitive.Item ref={ref} className={cn(className)} value={value} {...props} />
})
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName

const navigationMenuTriggerStyle = cva(
  'group text-body inline-flex p-xs w-max items-center justify-center rounded-xs bg-surface transition-colors focus-visible:bg-surface-container focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50'
)

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <BodyText asChild>
    <NavigationMenuPrimitive.Trigger ref={ref} className={cn(navigationMenuTriggerStyle(), className)} {...props}>
      {children}
    </NavigationMenuPrimitive.Trigger>
  </BodyText>
))
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content> & {
    contentFor: string
    align?: 'start' | 'center' | 'end'
  }
>(({ className, contentFor, align = 'center', ...props }, ref) => {
  const { activeItem, prevActiveItem, items } = useSelector(navigationMenuStore, (state) => state.context)

  const activeNavItemIndex = activeItem !== undefined ? items.indexOf(activeItem) : -1
  const prevActiveNavItemIndex = prevActiveItem !== undefined ? items.indexOf(prevActiveItem) : -1
  const index = items.indexOf(contentFor)
  const active = activeNavItemIndex !== -1 && activeNavItemIndex === index && activeItem === contentFor

  function getTransitionAnimation(): TransitionAnimation | undefined {
    if (
      prevActiveNavItemIndex === -1 ||
      activeNavItemIndex === -1 ||
      (index !== prevActiveNavItemIndex && index !== activeNavItemIndex)
    ) {
      return undefined
    }

    if (index === prevActiveNavItemIndex && activeNavItemIndex > prevActiveNavItemIndex) {
      return 'to-start'
    }

    if (index === prevActiveNavItemIndex && activeNavItemIndex < prevActiveNavItemIndex) {
      return 'to-end'
    }

    if (index === activeNavItemIndex && activeNavItemIndex > prevActiveNavItemIndex) {
      return 'from-end'
    }

    if (index === activeNavItemIndex && activeNavItemIndex < prevActiveNavItemIndex) {
      return 'from-start'
    }
  }
  const transitionAnimation = getTransitionAnimation()

  // Outer container variants - handles the layout shift
  const outerContainerVariants: Variants = {
    initial: (transitionAnimation: TransitionAnimation) => {
      return {
        opacity: transitionAnimation?.startsWith('from-') ? 1 : 0,
        y: transitionAnimation?.startsWith('from-') ? 0 : 10
      }
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: (transitionAnimation: TransitionAnimation) => ({
      opacity: transitionAnimation?.startsWith('to-') ? 1 : 0,
      y: transitionAnimation?.startsWith('to-') ? 0 : 10
    })
  }

  // Inner content variants - handles the sliding
  const innerContainerVariants: Variants = {
    initial: (transitionAnimation?: TransitionAnimation) => ({
      x: transitionAnimation === 'from-end' ? 200 : transitionAnimation === 'from-start' ? -200 : 0,
      opacity: 0
    }),
    animate: {
      x: 0,
      opacity: 1
    },
    // The exit animation uses a smaller translation value because it combines with
    // the container's layout animation. Using the same value as the initial animation
    // would result in an exaggerated movement, so we reduce it to achieve visual balance.
    exit: (transitionAnimation?: TransitionAnimation) => ({
      x: transitionAnimation === 'to-end' ? 50 : transitionAnimation === 'to-start' ? -50 : 0,
      opacity: 0
    })
  }

  return (
    <AnimatePresence custom={transitionAnimation}>
      {active ? (
        <div
          className={cn('pt-xs absolute z-50', {
            'left-0': align === 'start',
            'left-1/2 -translate-x-1/2': align === 'center',
            'right-0': align === 'end'
          })}
        >
          <NavigationMenuPrimitive.Content asChild ref={ref} {...props}>
            <motion.div
              variants={outerContainerVariants}
              custom={transitionAnimation}
              initial="initial"
              animate="animate"
              exit="exit"
              className={cn(
                'w-full md:w-auto',
                'bg-surface p-xs border-outline-variant min-w-32 overflow-hidden rounded-md border',
                className
              )}
              layoutId="navigation-menu-content"
              transition={{
                duration: 0.2,
                ease: 'easeOut'
              }}
            >
              <motion.div
                variants={innerContainerVariants}
                custom={transitionAnimation}
                initial="initial"
                animate="animate"
                exit="exit"
                layout="size"
                transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }}
              >
                {props.children}
              </motion.div>
            </motion.div>
          </NavigationMenuPrimitive.Content>
        </div>
      ) : null}
    </AnimatePresence>
  )
})
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName

const NavigationMenuLink = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Link>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Link>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Link
    ref={ref}
    className={cn(navigationMenuTriggerStyle(), 'hover:bg-surface-container justify-start text-left', className)}
    {...props}
  />
))
NavigationMenuLink.displayName = NavigationMenuPrimitive.Link.displayName

const NavigationMenuViewport = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <div className={cn('absolute left-0 top-full flex justify-center')}>
    <NavigationMenuPrimitive.Viewport
      className={cn(
        'origin-top-center bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border shadow-lg md:w-[var(--radix-navigation-menu-viewport-width)]',
        className
      )}
      ref={ref}
      {...props}
    />
  </div>
))
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName

const NavigationMenuIndicator = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    ref={ref}
    className={cn(
      'data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden',
      className
    )}
    {...props}
  >
    <div className="bg-border relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm shadow-md" />
  </NavigationMenuPrimitive.Indicator>
))
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName

export {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  NavigationMenuViewport,
  navigationMenuStore
}
